import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

import Socket from "Configuration/Socket";
import useQueueVoiceAnnouncement from "Hooks/useQueueVoiceAnnouncement";
import { API_SLUG } from "Store/Queue";

enum QueueStatusKeyMapper {
  PENDING = "10",
  DONE = "20",
  SKIPPED = "30",
  CANCELLED = "40",
}

interface QueueItem {
  uuid: string;
  currentQueueNumber: number;
  checkinAt: string;
  status: QueueStatusKeyMapper;
  patientName: string;
}

interface AdvertisementItem {
  id: string;
  type: "image" | "video";
  originalName: string;
  mimeType: string;
  filePath: string;
  createdAt: string;
  url: string;
}

interface AdvertisementInfo {
  enabled: boolean;
  items: AdvertisementItem[];
  updatedAt: string;
}

interface PublicDashboardInfo {
  currentQueue: QueueItem | null;
  currentCallingQueue: QueueItem | null;
  incomingQueues: QueueItem[];
  recentlyCalledQueues: QueueItem[];
  advertisement: AdvertisementInfo;
  updatedAt: string;
}

interface VoiceAnnouncement {
  key: string;
  text: string;
}

const defaultInfo: PublicDashboardInfo = {
  currentQueue: null,
  currentCallingQueue: null,
  incomingQueues: [],
  recentlyCalledQueues: [],
  advertisement: {
    enabled: false,
    items: [],
    updatedAt: "",
  },
  updatedAt: "",
};

const normalizeQueue = (queue: any): QueueItem => ({
  uuid: queue?.uuid ?? "",
  currentQueueNumber: Number(queue?.currentQueueNumber ?? 0),
  checkinAt: queue?.checkinAt ?? "",
  status: queue?.status ?? QueueStatusKeyMapper.PENDING,
  patientName: queue?.patientName ?? "",
});

const MAX_VISIBLE_QUEUES = 6;
const AUTO_PAGE_INTERVAL_MS = 8000;
const IMAGE_SLIDE_INTERVAL_MS = 8000;

const buildMediaUrl = (url: string) => {
  const baseUrl = (process.env.REACT_APP_API_ENDPOINT || "")
    .replace(/\/$/, "")
    .replace("/v1", "");

  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }

  return `${baseUrl}${url.startsWith("/") ? url : `/${url}`}`;
};

const normalizeAdvertisementItem = (item: any): AdvertisementItem => ({
  id: item?.id ?? "",
  type: item?.type === "video" ? "video" : "image",
  originalName: item?.originalName ?? "",
  mimeType: item?.mimeType ?? "",
  filePath: item?.filePath ?? "",
  createdAt: item?.createdAt ?? "",
  url: buildMediaUrl(item?.url ?? ""),
});

const ViewModel = () => {
  const [socket, setSocket] = useState<any>(null);
  const [isShowWaiting, setIsShowWaiting] = useState<boolean>(true);
  const [info, setInfo] = useState<PublicDashboardInfo>(defaultInfo);
  const [queuePageIndex, setQueuePageIndex] = useState<number>(0);
  const [advertisementIndex, setAdvertisementIndex] = useState<number>(0);
  const [voiceAnnouncement, setVoiceAnnouncement] =
    useState<VoiceAnnouncement | null>(null);

  const {
    isVoiceSupported,
    isVoiceMuted,
    isVoiceSpeaking,
    voiceErrorText,
    speakingAnnouncementText,
    onToggleVoiceMute,
    onTestVoiceCall,
    speakAnnouncement,
  } = useQueueVoiceAnnouncement({
    repeatCount: 2,
    repeatDelayMs: 1200,
    defaultMuted: true,
  });

  const hasHydratedRef = useRef<boolean>(false);
  const lastCallingQueueKeyRef = useRef<string>("");
  const lastPlayedAnnouncementKeyRef = useRef<string>("");

  const handleDashboardInfo = (data: any) => {
    const normalizedCurrentQueue = data?.currentQueue
      ? normalizeQueue(data.currentQueue)
      : null;

    const normalizedCurrentCallingQueue = data?.currentCallingQueue
      ? normalizeQueue(data.currentCallingQueue)
      : null;

    const normalizedIncomingQueues = Array.isArray(data?.incomingQueues)
      ? data.incomingQueues
          .map((queue: any) => normalizeQueue(queue))
          .sort(
            (a: QueueItem, b: QueueItem) =>
              a.currentQueueNumber - b.currentQueueNumber,
          )
      : [];

    const normalizedRecentlyCalledQueues = Array.isArray(
      data?.recentlyCalledQueues,
    )
      ? data.recentlyCalledQueues
          .map((queue: any) => normalizeQueue(queue))
          .filter(
            (queue: QueueItem) => queue.status === QueueStatusKeyMapper.DONE,
          )
      : [];

    const normalizedAdvertisementItems = Array.isArray(
      data?.advertisement?.items,
    )
      ? data.advertisement.items.map((item: any) =>
          normalizeAdvertisementItem(item),
        )
      : [];

    setInfo({
      currentQueue: normalizedCurrentQueue,
      currentCallingQueue: normalizedCurrentCallingQueue,
      incomingQueues: normalizedIncomingQueues,
      recentlyCalledQueues: normalizedRecentlyCalledQueues,
      advertisement: {
        enabled: Boolean(data?.advertisement?.enabled),
        items: normalizedAdvertisementItems,
        updatedAt: data?.advertisement?.updatedAt ?? "",
      },
      updatedAt: data?.updatedAt ?? "",
    });

    const nextCallingQueueKey = normalizedCurrentCallingQueue
      ? `${normalizedCurrentCallingQueue.uuid}:${normalizedCurrentCallingQueue.currentQueueNumber}:${normalizedCurrentCallingQueue.status}`
      : "";

    if (!hasHydratedRef.current) {
      hasHydratedRef.current = true;
      lastCallingQueueKeyRef.current = nextCallingQueueKey;
      setIsShowWaiting(false);
      return;
    }

    if (!nextCallingQueueKey) {
      lastCallingQueueKeyRef.current = "";
      setIsShowWaiting(false);
      return;
    }

    if (nextCallingQueueKey !== lastCallingQueueKeyRef.current) {
      const calledQueueNumber =
        normalizedCurrentCallingQueue?.currentQueueNumber ?? 0;
      lastCallingQueueKeyRef.current = nextCallingQueueKey;
      setVoiceAnnouncement({
        key: nextCallingQueueKey,
        text: `เชิญหมายเลข ${calledQueueNumber}`,
      });
    }

    setIsShowWaiting(false);
  };

  const getDashboard = async () => {
    try {
      const { data } = await axios.get(`${API_SLUG}/dashboard`);
      handleDashboardInfo(data);
    } catch (err) {
      setIsShowWaiting(false);
    }
  };

  const displayQueues = useMemo(() => {
    const currentCalledNumber =
      info.currentCallingQueue?.currentQueueNumber ?? 0;

    return info.incomingQueues.filter(
      (queue: QueueItem) =>
        queue.status === QueueStatusKeyMapper.PENDING &&
        queue.currentQueueNumber > currentCalledNumber,
    );
  }, [info.incomingQueues, info.currentCallingQueue]);

  const queuePageCount = useMemo(() => {
    return Math.max(1, Math.ceil(displayQueues.length / MAX_VISIBLE_QUEUES));
  }, [displayQueues]);

  const visibleQueues = useMemo(() => {
    const startIndex = queuePageIndex * MAX_VISIBLE_QUEUES;
    return displayQueues.slice(startIndex, startIndex + MAX_VISIBLE_QUEUES);
  }, [displayQueues, queuePageIndex]);

  const calledQueue = useMemo(() => {
    return info.currentCallingQueue;
  }, [info.currentCallingQueue]);

  const advertisementItems = useMemo(() => {
    if (!info.advertisement.enabled) return [];
    return info.advertisement.items;
  }, [info.advertisement.enabled, info.advertisement.items]);

  const currentAdvertisement = useMemo(() => {
    if (advertisementItems.length === 0) return null;
    return advertisementItems[advertisementIndex] ?? advertisementItems[0];
  }, [advertisementIndex, advertisementItems]);

  useEffect(() => {
    if (queuePageIndex <= queuePageCount - 1) return;
    setQueuePageIndex(0);
  }, [queuePageCount, queuePageIndex]);

  useEffect(() => {
    if (advertisementIndex <= advertisementItems.length - 1) return;
    setAdvertisementIndex(0);
  }, [advertisementIndex, advertisementItems.length]);

  useEffect(() => {
    if (queuePageCount <= 1) return;

    const intervalId = setInterval(() => {
      setQueuePageIndex((prev) => (prev + 1) % queuePageCount);
    }, AUTO_PAGE_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [queuePageCount]);

  useEffect(() => {
    if (advertisementItems.length <= 1) return;
    if (currentAdvertisement?.type !== "image") return;

    const timeoutId = window.setTimeout(() => {
      setAdvertisementIndex((currentIndex) =>
        currentIndex + 1 >= advertisementItems.length ? 0 : currentIndex + 1,
      );
    }, IMAGE_SLIDE_INTERVAL_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    advertisementItems.length,
    currentAdvertisement?.id,
    currentAdvertisement?.type,
  ]);

  useEffect(() => {
    if (!voiceAnnouncement) return;
    if (voiceAnnouncement.key === lastPlayedAnnouncementKeyRef.current) return;

    lastPlayedAnnouncementKeyRef.current = voiceAnnouncement.key;
    speakAnnouncement(voiceAnnouncement.text);
  }, [voiceAnnouncement, speakAnnouncement]);

  useEffect(() => {
    getDashboard();

    const socketConnection = Socket(undefined, {
      mode: "public-dashboard",
      clientType: "public-dashboard",
    });
    setSocket(socketConnection);

    return () => {
      socketConnection.disconnect();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!socket) return;

    socket.on("public-dashboard", handleDashboardInfo);
    socket.emit("public-dashboard:info");

    return () => {
      socket.off("public-dashboard");
    };
    // eslint-disable-next-line
  }, [socket]);

  const onAdvanceAdvertisement = useCallback(() => {
    if (advertisementItems.length <= 1) return;
    setAdvertisementIndex((currentIndex) =>
      currentIndex + 1 >= advertisementItems.length ? 0 : currentIndex + 1,
    );
  }, [advertisementItems.length]);

  const onSelectAdvertisement = useCallback((nextIndex: number) => {
    setAdvertisementIndex(nextIndex);
  }, []);

  return {
    isShowWaiting,
    visibleQueues,
    totalPendingQueues: displayQueues.length,
    queuePageCount,
    pageStart:
      displayQueues.length === 0 ? 0 : queuePageIndex * MAX_VISIBLE_QUEUES + 1,
    pageEnd: queuePageIndex * MAX_VISIBLE_QUEUES + visibleQueues.length,
    calledQueue,
    isVoiceSupported,
    isVoiceMuted,
    isVoiceSpeaking,
    voiceErrorText,
    speakingAnnouncementText,
    onToggleVoiceMute,
    onTestVoiceCall,
    updatedAt: info.updatedAt,
    advertisementEnabled: info.advertisement.enabled,
    advertisementItems,
    currentAdvertisement,
    currentAdvertisementIndex: advertisementIndex,
    onAdvanceAdvertisement,
    onSelectAdvertisement,
  };
};

export default ViewModel;
